import type { Dimension } from '@carboncopyco/types';
import type { IInnerTab } from '@carboncopyco/types/desktop';
import {
  type CSSProperties,
  type ReactNode,
  createContext,
  useContext,
  useState,
} from 'react';

const iPhone14ProUserAgent =
  'Mozilla/5.0 (iPhone; CPU iPhone OS 16_5_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/20F75 [FBAN/FBIOS;FBDV/iPhone14,4;FBMD/iPhone;FBSN/iOS;FBSV/16.5.1;FBSS/3;FBID/phone;FBLC/fr_FR;FBOP/5]';
const iPhone14ProViewportSize = { width: 393, height: 852 };

const clipPolygonInitialState = {
  x: 0,
  y: 0,
  width: iPhone14ProViewportSize.width,
  height: iPhone14ProViewportSize.height,
};

const deviceSizeInitialState = {
  width: null,
  height: null,
};

const zoomFactorInitialState = 1;

type ResponsiveModeContextType = {
  isResponsiveModeActive: boolean;
  setIsResponsiveModeActive: (
    isActive: boolean,
    innerTabId: IInnerTab['id'],
    dimension?: Dimension
  ) => void;
  deviceSize: {
    width: number | null;
    height: number | null;
  };
  responsiveModeBasedStyles?: {
    wrapper: CSSProperties;
    inner: CSSProperties;
    view: CSSProperties;
  };
  setDeviceSize: (dimension: {
    width: number | null;
    height: number | null;
  }) => void;
  responsiveModeBasedClasses?: {
    wrapper: string;
    inner: string;
    view: string;
  };
  setClippedAreaDimensions?: (dimensions: {
    x: number;
    y: number;
    width: number;
    height: number;
  }) => void;
  zoomFactor?: number;
  setInnerViewZoomFactor?: (
    factor: number,
    innerTabId: IInnerTab['id']
  ) => void;
  recalculateZoomFactor: (innerTabId: IInnerTab['id']) => void;
};

const contextDefaultValue: ResponsiveModeContextType = {
  isResponsiveModeActive: false,
  setIsResponsiveModeActive: () => {},
  deviceSize: deviceSizeInitialState,
  setDeviceSize: () => {},
  responsiveModeBasedStyles: {
    wrapper: {},
    inner: {},
    view: {},
  },
  responsiveModeBasedClasses: {
    wrapper: '',
    inner: '',
    view: '',
  },
  setClippedAreaDimensions: () => {},
  zoomFactor: zoomFactorInitialState,
  setInnerViewZoomFactor: () => {},
  recalculateZoomFactor: () => {},
};

const ResponsiveModeContext =
  createContext<ResponsiveModeContextType>(contextDefaultValue);

export const useResponsiveModeContext = () => {
  const context = useContext(ResponsiveModeContext);

  if (!context) {
    throw new Error(
      'useResponsiveModeContext must be used within a ResponsiveModeContextProvider'
    );
  }

  return context;
};

export const ResponsiveModeContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isResponsiveModeActive, setIsResponsiveModeActive] = useState(false);
  const [deviceSize, setDeviceSize] = useState<{
    width: number | null;
    height: number | null;
  }>(deviceSizeInitialState);

  const [zoomFactor, setZoomFactor] = useState(zoomFactorInitialState);
  const [clippedAreaDimensions, setClippedAreaDimensions] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>(clipPolygonInitialState);

  const setInnerViewZoomFactor = (
    newZoomFactor: number,
    innerTabId: IInnerTab['id']
  ) => {
    if (!innerTabId) return;
    window.dsDesktop.view.setZoomFactor(innerTabId, newZoomFactor);
    setZoomFactor(newZoomFactor);
  };

  const recalculateZoomFactor = (innerTabId: IInnerTab['id']) => {
    setInnerViewZoomFactor(zoomFactor, innerTabId);
  };

  const setDesktopMode = async (innerTabId: IInnerTab['id']) => {
    if (!innerTabId) return;
    const desktopUserAgent = await window.dsDesktop.view.getAppViewUserAgent();
    await window.dsDesktop.view.setUserAgent(innerTabId, desktopUserAgent);
    setInnerViewZoomFactor(zoomFactorInitialState, innerTabId);
  };

  const switchDevice = async (
    isResponsiveMode: boolean,
    innerTabId: IInnerTab['id'],
    dimension?: Dimension
  ) => {
    if (!innerTabId) return;

    if (isResponsiveMode) {
      await window.dsDesktop.view.setUserAgent(
        innerTabId,
        iPhone14ProUserAgent
      );
      if (!dimension) {
        setDeviceSize(iPhone14ProViewportSize);
      }
      return;
    }
    setDesktopMode(innerTabId);
  };

  const areaLeft = clippedAreaDimensions.x;
  const areaTop = clippedAreaDimensions.y;
  const areaRight = clippedAreaDimensions.x + clippedAreaDimensions.width;
  const areaBottom = clippedAreaDimensions.y + clippedAreaDimensions.height;
  const clippedAreaPolygon = `polygon(0 0, 0 100%, ${areaLeft}px 100%, ${areaLeft}px ${areaTop}px, ${areaRight}px ${areaTop}px, ${areaRight}px ${areaBottom}px, ${areaLeft}px ${areaBottom}px, ${areaLeft}px 100%, 100% 100%, 100% 0)`;

  const responsiveModeType = isResponsiveModeActive ? 'mobile' : 'desktop';

  const responsiveModeBasedClasses = {
    desktop: {
      wrapper: 'flex flex-grow flex-shrink basis-0 min-w-0',
      inner: 'flex flex-grow basis-0 min-w-0',
      view: 'flex-grow flex-shrink basis-0',
    },
    mobile: {
      wrapper: 'flex flex-grow flex-shrink basis-0 min-w-0 relative ',
      inner:
        'flex justify-center align-center absolute overflow-hidden inset-0 flex-grow basis-0 min-w-0',
      view: '',
    },
  }[responsiveModeType];

  const responsiveModeBasedStyles = {
    desktop: {
      wrapper: {} as CSSProperties,
      inner: {} as CSSProperties,
      view: {
        width: deviceSize?.width
          ? (deviceSize?.width ?? 0) * zoomFactor
          : '100%',
        height: deviceSize?.height
          ? (deviceSize?.height ?? 0) * zoomFactor
          : '100%',
      } as CSSProperties,
    },
    mobile: {
      wrapper: {
        clipPath: clippedAreaPolygon,
        backgroundColor: 'white',
      } as CSSProperties,
      inner: {} as CSSProperties,
      view: {
        width: deviceSize?.width
          ? (deviceSize?.width ?? 0) * zoomFactor
          : '100%',
        height: deviceSize?.height
          ? (deviceSize?.height ?? 0) * zoomFactor
          : '100%',
      } as CSSProperties,
    },
  }[responsiveModeType];

  const handleSetIsResponsiveModeActive = (
    isActive: boolean,
    innerTabId: IInnerTab['id'],
    dimension?: Dimension
  ) => {
    setIsResponsiveModeActive(isActive);
    switchDevice(isActive, innerTabId, dimension);
  };

  return (
    <ResponsiveModeContext.Provider
      value={{
        isResponsiveModeActive,
        setIsResponsiveModeActive: handleSetIsResponsiveModeActive,
        deviceSize,
        setDeviceSize,
        responsiveModeBasedStyles,
        responsiveModeBasedClasses,
        setClippedAreaDimensions,
        zoomFactor,
        setInnerViewZoomFactor,
        recalculateZoomFactor,
      }}
    >
      {children}
    </ResponsiveModeContext.Provider>
  );
};
